// src/components/shared/FormField.jsx
import React from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const FormField = ({
  type = 'text',
  name,
  label,
  value,
  onChange,
  onBlur,
  disabled = false,
  required = false,
  error = null,
  helperText = '',
  fullWidth = true,
  size = 'medium',
  options = [],
  showPassword = false,
  toggleShowPassword = null,
  startAdornment = null,
  endAdornment = null,
  multiline = false,
  rows = 4,
  placeholder = '',
  ...props
}) => {
  // Manejo de tipos específicos
  switch (type) {
    case 'select':
      return (
        <FormControl 
          fullWidth={fullWidth} 
          error={!!error} 
          disabled={disabled} 
          required={required}
          size={size}
        >
          <InputLabel id={`${name}-label`}>{label}</InputLabel>
          <Select
            labelId={`${name}-label`}
            id={name}
            name={name}
            value={value}
            label={label}
            onChange={onChange}
            onBlur={onBlur}
            {...props}
          >
            <MenuItem value="">-- Seleccione --</MenuItem>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value.toString()}>
                {option.label || option.value}
              </MenuItem>
            ))}
          </Select>
          {(error || helperText) && <FormHelperText>{error || helperText}</FormHelperText>}
        </FormControl>
      );
      
    case 'checkbox':
      return (
        <FormControlLabel
          control={
            <Checkbox
              name={name}
              checked={!!value}
              onChange={onChange}
              disabled={disabled}
              {...props}
            />
          }
          label={label}
        />
      );
      
    case 'radio':
      return (
        <FormControl component="fieldset" error={!!error} disabled={disabled}>
          <FormLabel component="legend">{label}</FormLabel>
          <RadioGroup
            name={name}
            value={value}
            onChange={onChange}
            {...props}
          >
            {options.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value.toString()}
                control={<Radio />}
                label={option.label || option.value}
              />
            ))}
          </RadioGroup>
          {(error || helperText) && <FormHelperText>{error || helperText}</FormHelperText>}
        </FormControl>
      );
      
    case 'switch':
      return (
        <FormControlLabel
          control={
            <Switch
              name={name}
              checked={!!value}
              onChange={onChange}
              disabled={disabled}
              {...props}
            />
          }
          label={label}
        />
      );
      
    case 'password':
      return (
        <TextField
          type={showPassword ? 'text' : 'password'}
          id={name}
          name={name}
          label={label}
          value={value || ''}
          onChange={onChange}
          onBlur={onBlur}
          error={!!error}
          helperText={error || helperText}
          required={required}
          disabled={disabled}
          fullWidth={fullWidth}
          size={size}
          placeholder={placeholder}
          InputProps={{
            startAdornment: startAdornment && (
              <InputAdornment position="start">{startAdornment}</InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {toggleShowPassword && (
                  <IconButton
                    onClick={toggleShowPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                )}
                {endAdornment}
              </InputAdornment>
            )
          }}
          {...props}
        />
      );
      
    // Para tipo texto, número, email, etc.
    default:
      return (
        <TextField
          type={type}
          id={name}
          name={name}
          label={label}
          value={value || ''}
          onChange={onChange}
          onBlur={onBlur}
          error={!!error}
          helperText={error || helperText}
          required={required}
          disabled={disabled}
          fullWidth={fullWidth}
          size={size}
          multiline={multiline}
          rows={multiline ? rows : undefined}
          placeholder={placeholder}
          InputProps={{
            startAdornment: startAdornment && (
              <InputAdornment position="start">{startAdornment}</InputAdornment>
            ),
            endAdornment: endAdornment && (
              <InputAdornment position="end">{endAdornment}</InputAdornment>
            )
          }}
          {...props}
        />
      );
  }
};

export default FormField;